import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
	Smartphone,
	CheckCircle2,
	AlertTriangle,
	Loader2,
	CircleDashed,
	Settings2,
	Star,
	QrCode,
	KeyRound,
	RefreshCw,
	Plus,
	Trash2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import AparelhoLigacoesModal from "../../components/AparelhoLigacoesModal";
import { Input } from "../../components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { recarregarConexoes } from "../../ligacoes/store";

// Cada conexão de WhatsApp e o aparelho de ligações dela. Pareia, confere se
// o número bate e liga/desliga a gravação — sem sair da página.
const Aparelhos = () => {
	const [lista, setLista] = useState(null);
	const [aberto, setAberto] = useState(null);
	const [gravando, setGravando] = useState(null);
	const [ocupado, setOcupado] = useState(null);
	const [codigos, setCodigos] = useState({});
	// Número digitado para parear por código quando a linha ainda não tem um.
	const [telefones, setTelefones] = useState({});
	// O QR só aparece quando a pessoa pede para escanear: um QR largado na
	// tela pode ser lido por qualquer celular que passe na frente.
	const [escaneando, setEscaneando] = useState({});
	const [novaLinha, setNovaLinha] = useState(false);
	const [nomeLinha, setNomeLinha] = useState("");
	const [excluir, setExcluir] = useState(null);

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/calls/devices");
			setLista(data);
		} catch (err) {
			toastError(err);
			setLista([]);
		}
	}, []);

	useEffect(() => {
		carregar();
		const socket = openSocket();
		socket.on("ligacao-aparelho", () => {
			carregar();
			recarregarConexoes();
		});
		return () => socket.disconnect();
	}, [carregar]);

	const alternarGravacao = async (c, v) => {
		setGravando(c.id);
		try {
			await api.put(`/calls/device/${c.id}/recording`, { enabled: v });
			toast.success(v ? `Gravação ligada em ${c.name}.` : `Gravação desligada em ${c.name}.`);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setGravando(null);
	};

	// Pareamento aqui mesmo: gera a sessão e o QR chega pelo socket, atualizado
	// a cada troca (o WhatsApp renova o QR de tempos em tempos).
	const gerarQr = async (c) => {
		setOcupado(`qr-${c.id}`);
		setCodigos((x) => ({ ...x, [c.id]: null }));
		setEscaneando((x) => ({ ...x, [c.id]: true }));
		try {
			await api.post(`/calls/device/${c.id}`);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	const pedirCodigo = async (c) => {
		setOcupado(`codigo-${c.id}`);
		try {
			if (!c.device?.exists) await api.post(`/calls/device/${c.id}`);
			const { data } = await api.post(`/calls/device/${c.id}/pair-code`, {
				phone: telefones[c.id] || undefined,
			});
			setCodigos((x) => ({ ...x, [c.id]: data.code }));
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	if (lista === null) {
		return (
			<div className="flex justify-center py-16">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Linha só de ligações: nasce e é pareada aqui, num QR só do motor de
	// ligações. Sem sessão de mensagens — o número entra ao parear.
	const criarLinha = async () => {
		setOcupado("linha");
		try {
			const { data } = await api.post("/calls/lines", { name: nomeLinha });
			setNovaLinha(false);
			setNomeLinha("");
			// Quem cria a linha já está com o celular na mão: mostra o QR.
			setEscaneando((x) => ({ ...x, [data.id]: true }));
			await carregar();
			recarregarConexoes();
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	// modo: "linha" (linha só de ligações), "aparelho" (tira só o aparelho de
	// ligações da conexão) ou "conexao" (apaga a conexão de WhatsApp inteira).
	const confirmarExclusao = async (modo) => {
		setOcupado(`excluir-${modo}`);
		try {
			if (modo === "linha") await api.delete(`/calls/lines/${excluir.id}`);
			else if (modo === "aparelho") await api.delete(`/calls/device/${excluir.id}`);
			else await api.delete(`/whatsapp/${excluir.id}`);
			toast.success(
				modo === "aparelho" ? `Aparelho de ligações de ${excluir.name} desconectado.` : `${excluir.name} excluída.`
			);
			setExcluir(null);
			await carregar();
			recarregarConexoes();
		} catch (err) {
			toastError(err);
		}
		setOcupado(null);
	};

	const modalNovaConexao = (
		<>
			<Dialog open={novaLinha} onOpenChange={setNovaLinha}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>Nova linha de ligações</DialogTitle>
						<DialogDescription>
							Um número do WhatsApp só para fazer e receber ligações, pareado direto no motor de ligações. Não
							recebe nem envia mensagens.
						</DialogDescription>
					</DialogHeader>
					<Input
						autoFocus
						placeholder="Nome (ex.: Central de ligações)"
						value={nomeLinha}
						onChange={(e) => setNomeLinha(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && criarLinha()}
					/>
					<DialogFooter>
						<Button variant="outline" onClick={() => setNovaLinha(false)}>
							Cancelar
						</Button>
						<Button disabled={ocupado === "linha"} onClick={criarLinha}>
							{ocupado === "linha" ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
							Criar e escanear QR
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<Dialog open={!!excluir} onOpenChange={(v) => !v && setExcluir(null)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Excluir {excluir?.name}?</DialogTitle>
						<DialogDescription>
							{excluir?.callsOnly
								? "O aparelho é desconectado do WhatsApp e a linha deixa de fazer e receber ligações. O histórico das ligações continua."
								: "Esta é uma conexão de WhatsApp com mensagens. Escolha o que remover."}
						</DialogDescription>
					</DialogHeader>
					{excluir && !excluir.callsOnly && (
						<div className="space-y-2">
							{excluir.device?.exists && (
								<button
									type="button"
									disabled={!!ocupado}
									onClick={() => confirmarExclusao("aparelho")}
									className="w-full rounded-lg border p-3 text-left hover:bg-accent disabled:opacity-50"
								>
									<p className="flex items-center gap-2 text-sm font-medium">
										{ocupado === "excluir-aparelho" && <Loader2 className="h-4 w-4 animate-spin" />}
										Só desconectar o aparelho de ligações
									</p>
									<p className="mt-0.5 text-xs text-muted-foreground">
										A conexão continua recebendo e enviando mensagens; só as ligações param.
									</p>
								</button>
							)}
							<button
								type="button"
								disabled={!!ocupado}
								onClick={() => confirmarExclusao("conexao")}
								className="w-full rounded-lg border border-destructive/40 p-3 text-left hover:bg-destructive/10 disabled:opacity-50"
							>
								<p className="flex items-center gap-2 text-sm font-medium text-destructive">
									{ocupado === "excluir-conexao" && <Loader2 className="h-4 w-4 animate-spin" />}
									Excluir a conexão inteira
								</p>
								<p className="mt-0.5 text-xs text-muted-foreground">
									Desconecta o WhatsApp deste número do sistema: param as mensagens e as ligações. Os
									atendimentos e o histórico de ligações continuam; as <b>campanhas e mensagens agendadas</b>{" "}
									desta conexão são apagadas junto.
								</p>
							</button>
						</div>
					)}
					<DialogFooter>
						<Button variant="outline" onClick={() => setExcluir(null)}>
							Cancelar
						</Button>
						{excluir?.callsOnly && (
							<Button variant="destructive" disabled={!!ocupado} onClick={() => confirmarExclusao("linha")}>
								{ocupado === "excluir-linha" && <Loader2 className="h-4 w-4 animate-spin" />}
								Excluir
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);

	if (!lista.length) {
		return (
			<div className="rounded-xl border bg-card px-4 py-16 text-center">
				<Smartphone className="mx-auto h-8 w-8 text-muted-foreground/50" />
				<p className="mt-3 text-sm font-medium">Nenhuma linha de ligações ainda</p>
				<p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
					Crie uma linha e escaneie o QR com o celular do número que vai fazer e receber as ligações.
				</p>
				<Button className="mt-4" onClick={() => setNovaLinha(true)}>
					<Plus className="h-4 w-4" />
					Nova linha de ligações
				</Button>
				{modalNovaConexao}
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="flex justify-end">
				<Button size="sm" onClick={() => setNovaLinha(true)}>
					<Plus className="h-3.5 w-3.5" />
					Nova linha de ligações
				</Button>
			</div>
			{modalNovaConexao}

			<div className="rounded-lg border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
				<b>Linha de ligações</b>: número pareado só no motor de ligações (um QR). <b>Conexão de WhatsApp</b>: pode
				ganhar um aparelho de ligações — o mesmo número conectado uma segunda vez, só para chamadas, e as
				mensagens seguem pela conexão. O WhatsApp aceita até 4 aparelhos por número.
			</div>

			<div className="grid gap-3 md:grid-cols-2">
				{lista.map((c) => {
					const d = c.device || {};
					const pareado = d.paired;
					return (
						<div key={c.id} className="flex flex-col rounded-xl border bg-card p-4">
							<div className="flex items-start justify-between gap-2">
								<div className="min-w-0">
									<p className="flex items-center gap-1.5 truncate font-medium">
										{c.name}
										{c.isDefault && (
											<Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-label="Conexão padrão" />
										)}
									</p>
									<p className="text-xs text-muted-foreground">
										{c.callsOnly ? "Só ligações · " : "WhatsApp · "}
										{c.number || "número aparece ao parear"}
									</p>
								</div>
								{pareado ? (
									<span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600">
										<CheckCircle2 className="h-3.5 w-3.5" /> Ligações ativas
									</span>
								) : d.exists ? (
									<span className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600">
										<CircleDashed className="h-3.5 w-3.5" /> Aguardando pareamento
									</span>
								) : (
									<span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
										Sem aparelho
									</span>
								)}
							</div>

							{d.numberMatches === false && (
								<p className="mt-2 flex gap-1.5 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
									<AlertTriangle className="h-4 w-4 shrink-0" />
									Pareado com outro número. As ligações sairiam de um número diferente desta conexão.
								</p>
							)}
							{!c.callsOnly && c.status !== "CONNECTED" && (
								<p className="mt-2 text-xs text-muted-foreground">
									A conexão de mensagens não está conectada agora; as ligações funcionam mesmo assim, pelo
									aparelho próprio.
								</p>
							)}

							{!pareado && d.engineRunning && (
								<div className="mt-3 rounded-lg border p-3">
									{!escaneando[c.id] && !codigos[c.id] ? (
										<p className="text-center text-xs text-muted-foreground">
											Clique em <b>Escanear QR Code</b> com o celular {c.number ? `de ${c.number}` : "deste número"} em mãos.
										</p>
									) : codigos[c.id] ? (
										<div className="text-center">
											<p className="text-xs text-muted-foreground">Código de pareamento</p>
											<p className="mt-1 font-mono text-2xl font-semibold tracking-[0.3em]">{codigos[c.id]}</p>
											<p className="mt-2 text-xs text-muted-foreground">
												No celular {c.number ? `de ${c.number}` : "deste número"}: WhatsApp › Aparelhos conectados › Conectar aparelho ›
												“Conectar com número de telefone”.
											</p>
										</div>
									) : d.exists && d.qr ? (
										<div className="flex flex-col items-center gap-2">
											<div className="rounded-md bg-white p-2">
												<QRCodeSVG value={d.qr} size={200} />
											</div>
											<p className="text-center text-xs text-muted-foreground">
												No celular <b>{c.number ? `de ${c.number}` : "deste número"}</b>: WhatsApp › Aparelhos conectados › Conectar
												aparelho, e aponte para o QR.
											</p>
										</div>
									) : d.exists ? (
										<div className="flex flex-col items-center gap-2 py-4 text-xs text-muted-foreground">
											<Loader2 className="h-5 w-5 animate-spin" />
											Gerando QR Code…
										</div>
									) : (
										<p className="text-xs text-muted-foreground">
											Gere o QR e leia com o celular deste número para ativar as ligações.
										</p>
									)}
									<div className="mt-3 flex flex-wrap justify-center gap-2">
										<Button size="sm" disabled={!!ocupado} onClick={() => gerarQr(c)}>
											{ocupado === `qr-${c.id}` ? (
												<Loader2 className="h-3.5 w-3.5 animate-spin" />
											) : escaneando[c.id] ? (
												<RefreshCw className="h-3.5 w-3.5" />
											) : (
												<QrCode className="h-3.5 w-3.5" />
											)}
											{escaneando[c.id] ? "Novo QR" : "Escanear QR Code"}
										</Button>
										{!c.number && !codigos[c.id] && (
											<Input
												inputMode="tel"
												placeholder="Nº do celular (DDI+DDD)"
												className="h-8 w-44 text-xs"
												value={telefones[c.id] || ""}
												onChange={(e) => setTelefones((x) => ({ ...x, [c.id]: e.target.value }))}
											/>
										)}
										{(escaneando[c.id] || codigos[c.id]) && (
											<Button
												size="sm"
												variant="ghost"
												onClick={() => {
													setEscaneando((x) => ({ ...x, [c.id]: false }));
													setCodigos((x) => ({ ...x, [c.id]: null }));
												}}
											>
												Cancelar
											</Button>
										)}
										{(
											<Button
												size="sm"
												variant="outline"
												disabled={!!ocupado || (!c.number && String(telefones[c.id] || "").replace(/\D/g, "").length < 10)}
												title={!c.number ? "Digite o número do celular ao lado" : undefined}
												onClick={() => (codigos[c.id] ? setCodigos((x) => ({ ...x, [c.id]: null })) : pedirCodigo(c))}
											>
												{ocupado === `codigo-${c.id}` ? (
													<Loader2 className="h-3.5 w-3.5 animate-spin" />
												) : (
													<KeyRound className="h-3.5 w-3.5" />
												)}
												{codigos[c.id] ? "Voltar ao QR" : "Parear por código"}
											</Button>
										)}
									</div>
								</div>
							)}

							<div className="mt-3 flex items-center justify-between border-t pt-3">
								<label className="flex items-center gap-2 text-xs">
									<Switch
										checked={Boolean(d.recording)}
										disabled={gravando === c.id}
										onCheckedChange={(v) => alternarGravacao(c, v)}
									/>
									Gravar ligações
								</label>
								<div className="flex gap-1">
									<Button
										size="icon"
										variant="ghost"
										className="h-8 w-8 text-destructive hover:text-destructive"
										aria-label={`Excluir ${c.name}`}
										title={c.callsOnly ? "Excluir linha" : "Excluir conexão ou aparelho"}
										onClick={() => setExcluir(c)}
									>
										<Trash2 className="h-4 w-4" />
									</Button>
									<Button size="sm" variant="outline" onClick={() => setAberto(c)}>
										<Settings2 className="h-3.5 w-3.5" />
										{pareado ? "Gerenciar" : "Opções"}
									</Button>
								</div>
							</div>
						</div>
					);
				})}
			</div>

			<AparelhoLigacoesModal
				open={Boolean(aberto)}
				whatsApp={aberto}
				onClose={() => {
					setAberto(null);
					carregar();
					recarregarConexoes();
				}}
			/>
		</div>
	);
};

export default Aparelhos;
